import React, { useState, useEffect } from "react";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { ArrowRight, X, Gift, CheckCircle2, AlertCircle } from "lucide-react";
import { useContactMutation } from "@/utils/useContactMutation";
import COUNTRY_DATA from "@/utils/countryCode.json";
import { toast } from "sonner";
import Select from "../../Select";
import { AppButton } from "../AppButton";
import { INITIAL_STATE } from "@/constant/contactForm";
import { validateContactForm } from "@/utils/validateContactForm";


const WelcomeModal = ({ show, onClose, onSubmit }) => {
    const [formData, setFormData] = useState(INITIAL_STATE);
    const [errors, setErrors] = useState({});
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const { mutate, isPending } = useContactMutation();

    const getCountryObj = (name) => COUNTRY_DATA.find((c) => c.name === name);

    // Manage body overflow when modal is open
    useEffect(() => {
        const isModalOpen = show || showSuccessAlert || showErrorAlert;

        if (isModalOpen) {
            // Store original overflow value
            const originalOverflow = document.body.style.overflowY;
            document.body.style.overflowY = 'hidden';

            // Cleanup: restore original overflow when modal closes
            return () => {
                document.body.style.overflowY = originalOverflow;
            };
        }
    }, [show, showSuccessAlert, showErrorAlert]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "country_name") {
            const country = getCountryObj(value);
            setFormData({
                ...formData,
                country_name: value,
                country_code: country ? country.dial_code : "",
            });
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
        setErrors({
            ...errors,
            [name]: undefined,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const validationErrors = validateContactForm(formData, { validateCountry: false });
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            toast("Please fix the errors in the form.", {
                type: "error",
                duration: 3000,
            });
            return;
        }
        mutate(formData, {
            onSuccess: () => {
                setFormData(INITIAL_STATE);
                setShowSuccessAlert(true);
                onSubmit();
            },
            onError: (err) => {
                setErrorMessage(err.message || "Something went wrong. Please try again.");
                setShowErrorAlert(true);
            },
        });
    };

    const handleSuccessClose = () => {
        setShowSuccessAlert(false);
        onClose();
    };

    const handleErrorClose = () => {
        setShowErrorAlert(false);
    };

    // Success Alert Modal
    if (showSuccessAlert) {
        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-4 md:p-6 text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 className="w-12 h-12 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-black text-gray-900 mb-3">Success! 🎉</h3>
                    <p className="text-gray-600 mb-6">
                        Thank you for reaching out! We've received your request and will get back to you within 24 hours.
                    </p>
                    <AppButton
                        onClick={handleSuccessClose}
                        variant="gradient"
                        fullWidth
                    >
                        Got it!
                    </AppButton>
                </div>
            </div>
        );
    }

    // Error Alert Modal
    if (showErrorAlert) {
        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-4 md:p-6 text-center">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <AlertCircle className="w-12 h-12 text-red-600" />
                    </div>
                    <h3 className="text-2xl font-black text-gray-900 mb-3">Oops! Something went wrong</h3>
                    <p className="text-gray-600 mb-6">
                        {errorMessage}
                    </p>
                    <div className="flex gap-3">
                        <AppButton
                            onClick={handleErrorClose}
                            variant="outline"
                            fullWidth
                        >
                            Try Again
                        </AppButton>
                        <AppButton
                            onClick={() => {
                                handleErrorClose();
                                onClose();
                            }}
                            variant="gradient"
                            fullWidth
                        >
                            Close
                        </AppButton>
                    </div>
                </div>
            </div>
        );
    }

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full relative">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
                >
                    <X className="w-6 h-6" />
                </button>

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 md:p-6 text-center rounded-t-3xl">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto">
                        <Gift className="w-8 h-8" />
                    </div>
                    <h2 className="text-3xl font-black mb-2">Welcome! 🎉</h2>
                    <p className="text-blue-100 text-lg">
                        Get a <span className="font-bold text-yellow-300">FREE consultation</span> + <span className="font-bold text-yellow-300">40% OFF</span> your first project!
                    </p>
                </div>

                {/* Form */}
                <div className="p-4 md:p-6">
                    <p className="text-gray-600 text-center mb-6 text-sm">
                        Fill out the form below and we'll get back to you within 24 hours with a custom quote.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                            {/* Name */}
                            <div className="flex-grow">
                                <Input
                                    type="text"
                                    name="name"
                                    placeholder="Your Name *"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full"
                                />
                                {errors.name && (
                                    <div className="text-red-600 text-xs mt-1">{errors.name}</div>
                                )}
                            </div>

                            {/* Email */}
                            <div className="flex-grow">
                                <Input
                                    type="email"
                                    name="email"
                                    placeholder="Your Email *"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full"
                                />
                                {errors.email && (
                                    <div className="text-red-600 text-xs mt-1">{errors.email}</div>
                                )}
                            </div>
                        </div>

                        {/* Phone & Country Code */}
                        <div className="grid grid-cols-3 gap-2">
                            <div className="col-span-1">
                                <Select
                                    name="country_code"
                                    value={formData.country_code}
                                    onChange={handleInputChange}
                                    required
                                    className={'text-sm'}
                                >
                                    {COUNTRY_DATA.slice(0, 10).map((country) => (
                                        <option key={country.code} value={country.dial_code}>
                                            {country.flag} {country.dial_code}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                            <div className="col-span-2">
                                <Input
                                    type="text"
                                    name="phone"
                                    placeholder="Phone Number *"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full"
                                />
                            </div>
                        </div>
                        {errors.phone && (
                            <div className="text-red-600 text-xs mt-1">{errors.phone}</div>
                        )}

                        {/* Service */}
                        <div>
                            <Select
                                name="looking_for"
                                value={formData.looking_for}
                                onChange={handleInputChange}
                                required
                                className={'text-sm'}
                            >
                                <option value="">What service do you need? *</option>
                                <option value="Website Design">Website Design</option>
                                <option value="Website Redesign">Website Redesign</option>
                                <option value="Landing Page">Landing Page</option>
                                <option value="Ecommerce">E-commerce Website</option>
                                <option value="SEO">SEO Optimization</option>
                                <option value="Other">Other</option>
                            </Select>
                        </div>

                        {/* Message */}
                        <div>
                            <Textarea
                                name="comment"
                                placeholder="Tell us about your project... *"
                                value={formData.comment}
                                onChange={handleInputChange}
                                rows={3}
                                className="w-full"
                            />
                            {errors.comment && (
                                <div className="text-red-600 text-xs mt-1">{errors.comment}</div>
                            )}
                        </div>

                        {/* Submit */}
                        <AppButton
                            type="submit"
                            fullWidth
                            variant="gradient"
                            disabled={isPending}
                            className="mt-4"
                        >
                            {isPending ? (
                                "Submitting..."
                            ) : (
                                <>
                                    Claim My 40% OFF <ArrowRight className="ml-2 h-5 w-5" />
                                </>
                            )}
                        </AppButton>

                        <p className="text-xs text-gray-500 text-center">
                            🔒 Your information is safe and secure. We respect your privacy.
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default WelcomeModal;
